<template>
  <div class="bg-white rounded-lg border border-border">
    <!-- Card Header -->
    <div class="flex items-center gap-2 px-6 py-4 border-b border-border">
      <CreditCard class="w-4 h-4 text-muted-foreground" />
      <h3 class="text-sm font-semibold text-gray-800">Payment Methods</h3>
    </div>

    <!-- Card Body -->
    <div class="px-6 py-5 space-y-4">
      <!-- Processing Checkout State -->
      <div v-if="isProcessingCheckout" class="flex flex-col items-center justify-center py-8 gap-3">
        <div class="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-cosmicPurple"></div>
        <p class="text-sm text-muted-foreground">Processing checkout…</p>
      </div>

      <PaymentMethodList
        v-if="!isProcessingCheckout"
        :payment-methods="paymentMethods"
        :is-loading="isLoading"
        @add-payment-method="handleAddPaymentMethod"
        @payment-method-updated="handlePaymentMethodUpdated"
        @default-set-success="handleDefaultSetSuccess"
        @error="handleError"
      />

      <!-- Success: Card Added -->
      <div v-if="checkoutSuccess && !isProcessingCheckout" class="flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
        <div class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 shrink-0">
          <Check class="w-3.5 h-3.5 text-green-600" />
        </div>
        <p class="text-sm text-green-800 font-medium">Payment method added successfully.</p>
      </div>

      <!-- Success: Default Updated -->
      <div v-if="defaultSetSuccess && !isProcessingCheckout" class="flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 px-4 py-3">
        <div class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 shrink-0">
          <Check class="w-3.5 h-3.5 text-green-600" />
        </div>
        <p class="text-sm text-green-800 font-medium flex-1">Default payment method updated.</p>
        <button @click="defaultSetSuccess = false" class="text-green-500 hover:text-green-700 transition-colors">
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Error -->
      <div v-if="displayError && !isProcessingCheckout" class="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
        <AlertTriangle class="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
        <p class="text-sm text-red-800 flex-1">{{ displayError }}</p>
        <button @click="clearError" class="text-red-400 hover:text-red-600 transition-colors">
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Check, AlertTriangle, X, CreditCard } from 'lucide-vue-next'
import { usePaymentMethodsStore } from '@/stores/subscription'
import { useSubscriptionActions } from '@/composables/useSubscriptionActions'
import PaymentMethodList from '@/components/subscription/PaymentMethod/PaymentMethodList.vue'
import type { PaymentMethod } from '@/types/subscription'

const props = defineProps<{
  paymentMethods: PaymentMethod[]
  isLoading: boolean
  isProcessingCheckout: boolean
  checkoutSuccess: boolean
  checkoutError: string | null
}>()

const emit = defineEmits<{
  (e: 'payment-method-updated'): void
  (e: 'update:checkoutError', value: string | null): void
}>()

const paymentMethodsStore = usePaymentMethodsStore()
const { ensureStripeCustomer, openCheckoutSession, extractErrorMessage } = useSubscriptionActions()

const localError = ref<string | null>(null)
const defaultSetSuccess = ref(false)

const displayError = computed(() => props.checkoutError || localError.value)

const clearError = () => {
  localError.value = null
  emit('update:checkoutError', null)
}

const handleError = (message: string) => {
  localError.value = message
}

const handlePaymentMethodUpdated = () => {
  emit('payment-method-updated')
}

const handleDefaultSetSuccess = () => {
  defaultSetSuccess.value = true
  // Auto-hide success message after 3 seconds
  setTimeout(() => {
    defaultSetSuccess.value = false
  }, 3000)
}

const handleAddPaymentMethod = async () => {
  clearError()

  try {
    await ensureStripeCustomer()
    await openCheckoutSession(() => paymentMethodsStore.createSetupCardSession())
  } catch (error: any) {
    localError.value = extractErrorMessage(error, 'Failed to create checkout session')
  }
}
</script>
